import { create } from 'zustand';
import { subDays } from 'date-fns';
import type { RequestFilters, FilterOptions } from '../types';

interface FilterState {
  filters: RequestFilters;
  availableOptions: FilterOptions;
  availableAgentNames: string[];
  isOptionsLoaded: boolean;

  setDateRange: (startDate: Date | null, endDate: Date | null) => void;
  setRequestTypes: (types: string[]) => void;
  setOrgIds: (orgIds: string[]) => void;
  setUserIds: (userIds: string[]) => void;
  setTags: (tags: string[]) => void;
  setSearch: (search: string) => void;
  setSuccessOnly: (successOnly: boolean) => void;
  setFeedbackTypes: (feedbackTypes: string[]) => void;
  setChecklistOnly: (checklistOnly: boolean) => void;
  setSurfaces: (surfaces: string[]) => void;
  setAgentNames: (agentNames: string[]) => void;
  setExcludeScheduled: (excludeScheduled: boolean) => void;
  setAvailableOptions: (options: FilterOptions) => void;
  setAvailableAgentNames: (agentNames: string[]) => void;
  clearFilters: () => void;
  getFiltersForApi: () => Partial<RequestFilters>;
}

const defaultFilters: RequestFilters = {
  startDate: subDays(new Date(), 7),
  endDate: new Date(),
  envs: [],
  requestTypes: [],
  endpoints: [],
  orgIds: [],
  userIds: [],
  tags: [],
  search: '',
  successOnly: false,
  feedbackTypes: [],
  checklistOnly: false,
  surfaces: [],
  agentNames: [],
  excludeScheduled: false,
};

export const useFilterStore = create<FilterState>((set, get) => ({
  filters: { ...defaultFilters },
  availableOptions: {
    requestTypes: [],
    orgIds: [],
    userIds: [],
    tags: [],
  },
  availableAgentNames: [],
  isOptionsLoaded: false,

  setDateRange: (startDate, endDate) =>
    set((state) => ({
      filters: { ...state.filters, startDate, endDate },
    })),

  setRequestTypes: (requestTypes) =>
    set((state) => ({
      filters: { ...state.filters, requestTypes },
    })),

  setOrgIds: (orgIds) =>
    set((state) => ({
      filters: { ...state.filters, orgIds },
    })),

  setUserIds: (userIds) =>
    set((state) => ({
      filters: { ...state.filters, userIds },
    })),

  setTags: (tags) =>
    set((state) => ({
      filters: { ...state.filters, tags },
    })),

  setSearch: (search) =>
    set((state) => ({
      filters: { ...state.filters, search },
    })),

  setSuccessOnly: (successOnly) =>
    set((state) => ({
      filters: { ...state.filters, successOnly },
    })),

  setFeedbackTypes: (feedbackTypes) =>
    set((state) => ({
      filters: { ...state.filters, feedbackTypes },
    })),

  setChecklistOnly: (checklistOnly) =>
    set((state) => ({
      filters: { ...state.filters, checklistOnly },
    })),

  setSurfaces: (surfaces) =>
    set((state) => ({
      filters: { ...state.filters, surfaces },
    })),

  setAgentNames: (agentNames) =>
    set((state) => ({
      filters: { ...state.filters, agentNames },
    })),

  setExcludeScheduled: (excludeScheduled) =>
    set((state) => ({
      filters: { ...state.filters, excludeScheduled },
    })),

  setAvailableOptions: (options) => set({ availableOptions: options, isOptionsLoaded: true }),

  setAvailableAgentNames: (agentNames) => set({ availableAgentNames: agentNames }),

  clearFilters: () => set({ filters: { ...defaultFilters } }),

  getFiltersForApi: () => {
    const { filters } = get();
    const apiFilters: Partial<RequestFilters> = {};

    if (filters.startDate) apiFilters.startDate = filters.startDate;
    if (filters.endDate) apiFilters.endDate = filters.endDate;
    if (filters.requestTypes.length > 0) apiFilters.requestTypes = filters.requestTypes;
    if (filters.orgIds.length > 0) apiFilters.orgIds = filters.orgIds;
    if (filters.userIds.length > 0) apiFilters.userIds = filters.userIds;
    if (filters.tags.length > 0) apiFilters.tags = filters.tags;
    if (filters.search) apiFilters.search = filters.search;
    if (filters.successOnly) apiFilters.successOnly = filters.successOnly;
    if (filters.feedbackTypes.length > 0) apiFilters.feedbackTypes = filters.feedbackTypes;
    if (filters.checklistOnly) apiFilters.checklistOnly = filters.checklistOnly;
    if (filters.surfaces.length > 0) apiFilters.surfaces = filters.surfaces;
    if (filters.agentNames && filters.agentNames.length > 0) apiFilters.agentNames = filters.agentNames;
    if (filters.excludeScheduled) apiFilters.excludeScheduled = filters.excludeScheduled;

    return apiFilters;
  },
}));
